import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import api from '../utils/api';
import { normalizeCity, getNearbyCities } from '../utils/indianCities';

export const CityContext = createContext();

const CACHE_VERSION = 'v2'; // Increment to invalidate old caches
const CACHE_KEY = `userCity_${CACHE_VERSION}`;
const CACHE_EXPIRY_KEY = `userCityExpiry_${CACHE_VERSION}`;
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

/**
 * City Context Provider
 * Manages city detection and selection across the app
 */
export function CityProvider({ children }) {
  const [detectedCity, setDetectedCity] = useState(null);
  const [nearbyCities, setNearbyCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [isDetecting, setIsDetecting] = useState(false);
  const [error, setError] = useState(null);
  const [includeNearby, setIncludeNearby] = useState(true);

  // Get cached city data
  const getCachedCity = useCallback(() => {
    try {
      const expiry = localStorage.getItem(CACHE_EXPIRY_KEY);
      if (expiry && Date.now() < parseInt(expiry, 10)) {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          return JSON.parse(cached);
        }
      }
    } catch (e) {
      console.error('Error reading cached city:', e);
    }
    return null;
  }, []);

  // Cache city data
  const cacheCity = useCallback((city, nearby) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ city, nearby }));
      localStorage.setItem(CACHE_EXPIRY_KEY, (Date.now() + CACHE_TTL).toString());
    } catch (e) {
      console.error('Error caching city:', e);
    }
  }, []);

  // Reverse geocode coordinates to get city
  const reverseGeocode = useCallback(async (lat, lng) => {
    try {
      const response = await api.get('/geolocation/reverse', {
        params: { lat, lng },
      });

      if (response.data && !response.data.error) {
        const { city, nearbyCities: nearby } = response.data.data;
        return { city, nearby };
      }
      return null;
    } catch (e) {
      console.error('Reverse geocoding error:', e);
      return null;
    }
  }, []);

  // Detect user's city using geolocation
  const detectCity = useCallback(async () => {
    // Check cache first
    const cached = getCachedCity();
    if (cached) {
      setDetectedCity(cached.city);
      setNearbyCities(cached.nearby || getNearbyCities(cached.city));
      return cached.city;
    }

    if (!navigator.geolocation) {
      setError('Geolocation is not supported');
      return null;
    }

    setIsDetecting(true);
    setError(null);

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const result = await reverseGeocode(latitude, longitude);

          if (result && result.city) {
            const normalizedCity = normalizeCity(result.city);
            const nearby = result.nearby || getNearbyCities(normalizedCity);

            setDetectedCity(normalizedCity);
            setNearbyCities(nearby);
            cacheCity(normalizedCity, nearby);
            setIsDetecting(false);
            resolve(normalizedCity);
          } else {
            setError('Could not determine city');
            setIsDetecting(false);
            resolve(null);
          }
        },
        (geoError) => {
          console.error('Geolocation error:', geoError);
          setError('Location access denied');
          setIsDetecting(false);
          resolve(null);
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: CACHE_TTL,
        }
      );
    });
  }, [getCachedCity, reverseGeocode, cacheCity]);

  // Select a city manually
  const selectCity = useCallback((city) => {
    if (city) {
      const normalizedCity = normalizeCity(city);
      setSelectedCity(normalizedCity);
    } else {
      // Clear selection (show all cities)
      setSelectedCity(null);
    }
  }, []);

  // Get the effective city (selected or detected)
  const effectiveCity = selectedCity || detectedCity;

  // Clear city selection and cached data
  const clearCity = useCallback(() => {
    setSelectedCity(null);
    setDetectedCity(null);
    setNearbyCities([]);
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_EXPIRY_KEY);
  }, []);

  // Auto-detect city on mount
  useEffect(() => {
    detectCity();
  }, [detectCity]);

  const value = {
    // State
    detectedCity,
    selectedCity,
    effectiveCity,
    nearbyCities,
    isDetecting,
    error,
    includeNearby,

    // Actions
    detectCity,
    selectCity,
    clearCity,
    setIncludeNearby,
  };

  return <CityContext.Provider value={value}>{children}</CityContext.Provider>;
}

CityProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Custom hook to use the City context
 */
export function useCityContext() {
  const context = useContext(CityContext);
  if (!context) {
    throw new Error('useCityContext must be used within a CityProvider');
  }
  return context;
}
